import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Priority } from '@enview/interface/types/triage/Priority';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import ArrowDown from '../../../components/svg/ArrowDown';
import WorkspacePicker from '../DropDowns/WorkspacePicker';
import { TeamAPI, BillTriageAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';
import '../DropDowns/DropDown.scss';
import './PriorityColumnField.scss';

const PRIORITY_OPTIONS = [Priority.High, Priority.Medium, Priority.Low];

const priorityClass = (priority: Priority): string => priority.toLowerCase();

interface PriorityColumnFieldProps {
  billId: string;
  billWorkspaces?: BillSummary['workspaces'];
  customFields?: BillSummary['customFields'];
}

const PriorityColumnField = (props: PriorityColumnFieldProps): ReactElement => {
  const { billId, billWorkspaces = [], customFields } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIds = billWorkspaces
    .map((workspace) => workspace.id)
    .filter((id): id is number => id !== undefined);
  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    billWorkspaceIds,
    currentTeamModeId,
  );

  const priority =
    orgId && teamId
      ? (customFields?.[orgId]?.[teamId]?.Priority?.fieldValue as Priority | undefined)
      : undefined;

  const [updateBillPriority, { isLoading: priorityUpdating }] =
    BillTriageAPI.endpoints.updateBillPriority.useMutation();
  const [setWorkspaces, { isLoading: workspacesSaving }] =
    BillTriageAPI.endpoints.setBillWorkspaces.useMutation();
  const isMutating = priorityUpdating || workspacesSaving;

  // A priority picked while the bill isn't in any of the user's workspaces yet is held
  // here and applied once the forced-open WorkspacePicker below resolves a workspace to
  // write it to, instead of silently no-oping.
  const [pendingPriority, setPendingPriority] = useState<Priority | null>(null);

  const [displayed, setDisplayed] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = useCallback((): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPosition({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const handleToggle = (): void => {
    if (!displayed) updatePanelPosition();
    setDisplayed((prevDisplayed) => !prevDisplayed);
  };

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
      return;
    }
    setDisplayed(false);
  }, []);

  useEffect(() => {
    if (!displayed) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [displayed, handleClickOutside, updatePanelPosition]);

  const handleSelect = (value: Priority): void => {
    setDisplayed(false);
    if (!orgId || value === priority || isMutating) return;
    if (!teamId) {
      setPendingPriority(value);
      return;
    }
    void updateBillPriority({ orgId, billId, teamId, priority: value });
  };

  const handleSaveWorkspaces = async (workspaceIds: number[]): Promise<void> => {
    if (!orgId) return;
    await setWorkspaces({ orgId, billId, teamIds: workspaceIds }).unwrap();

    if (pendingPriority) {
      const newTeamId = getPrimaryWorkspaceId(
        userWorkspaces,
        workspaceIds,
        currentTeamModeId,
      );
      if (newTeamId) {
        await updateBillPriority({
          orgId,
          billId,
          teamId: newTeamId,
          priority: pendingPriority,
        }).unwrap();
      }
      setPendingPriority(null);
    }
  };

  return (
    <div className="priority-field">
      <div className="pf-control">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`pf-pill ${
            priority ? `pf-pill-${priorityClass(priority)}` : 'pf-pill-unset'
          } ${isMutating ? 'pf-pill-disabled' : ''}`}
          onClick={isMutating ? undefined : handleToggle}
          ref={triggerRef}
        >
          <span className="pf-pill-label">{priority || 'Set priority'}</span>
          <ArrowDown className="pf-pill-caret" />
        </div>

        {displayed &&
          createPortal(
            // "show" opts the options into the shared DropDown.scss item styles, matching
            // SingleSelectDropDown; the panel itself stays a fixed-position portal so it
            // isn't clipped by the table.
            <div
              className="pf-dropdown-panel show"
              ref={panelRef}
              style={{
                position: 'fixed',
                top: panelPosition.top,
                left: panelPosition.left,
              }}
            >
              {PRIORITY_OPTIONS.map((option) => (
                <button
                  className={`dropdown-item ${priority === option ? 'checked' : ''} ${
                    isMutating ? 'pf-dropdown-item-disabled' : ''
                  }`}
                  disabled={isMutating}
                  key={option}
                  onClick={() => handleSelect(option)}
                  type="button"
                >
                  <div
                    className={`dropdown-item-text pf-option-${priorityClass(option)}`}
                  >
                    {option}
                  </div>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>

      <WorkspacePicker
        billWorkspaceIds={billWorkspaceIds}
        forceOpen={!!pendingPriority}
        onForceClose={() => setPendingPriority(null)}
        onSave={handleSaveWorkspaces}
        sectionLabel={
          pendingPriority
            ? `SELECT A WORKSPACE TO MARK AS ${pendingPriority.toUpperCase()}...`
            : 'APPLY PRIORITY IN...'
        }
        trigger={<ArrowDown className="pf-workspace-caret" />}
        workspaces={userWorkspaces}
      />
    </div>
  );
};

export default PriorityColumnField;
